import * as Select from '@radix-ui/react-select';
import type { JSX, ReactNode } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { chevron } from '~/chords/DropdownSingleSelect/styles';
import type {
  DropdownState,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { Icon } from '~/chords/Icon';
import { cn } from '~/lib/cn';

interface DropdownTriggerProps extends RadixUiProps {
  id?: string;
  placeholder?: string;
  selectedOption?: InternalDropdownOption;
  isOpen: boolean;
  state?: DropdownState;
  disabled?: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export const DropdownTrigger = forwardRef<
  HTMLButtonElement,
  DropdownTriggerProps
>(
  (
    {
      id,
      placeholder,
      selectedOption,
      isOpen,
      state,
      disabled,
      OptionTemplate,
      radix = {},
    },
    ref,
  ) => {
    const { t } = useTranslation('common');

    const placeholderText = placeholder || t('dropdownTree.placeholder');
    let valueDisplay: JSX.Element | ReactNode = placeholderText;

    if (selectedOption) {
      if (OptionTemplate) {
        valueDisplay = (
          <OptionTemplate option={selectedOption} isTrigger={true} />
        );
      } else {
        valueDisplay = (
          <span className="truncate text-default">
            {selectedOption.label || selectedOption.value}
          </span>
        );
      }
    }

    return (
      <Select.Trigger
        {...radix.selectTrigger}
        ref={ref}
        id={id}
        className={cn(
          'relative flex w-full cursor-pointer items-stretch justify-between gap-sm rounded-xs py-xs pr-sm text-body-sm leading-8',
          'outline outline-1 transition-colors-with-outline duration-300 ease-out bg-emphasis-default outline-emphasis-subtle',
          'enabled:hover:bg-emphasis-subtle enabled:hover:outline-emphasis-strong disabled:cursor-not-allowed disabled:outline-transparent',
          {
            'pl-md': !(OptionTemplate && selectedOption),
            'outline-error-default': state === 'error',
            'outline-success-default': state === 'success',
            'outline-warning-default': state === 'warning',
          },
          radix.selectTrigger?.className,
        )}
        aria-invalid={state === 'error'}
      >
        <Select.Value
          {...radix.selectValue}
          placeholder={
            <div className="flex min-w-0 items-center text-subtle">
              <div className="truncate">{placeholderText}</div>
            </div>
          }
          asChild
        >
          <div
            className={cn(
              {
                'text-disabled': disabled,
              },
              'flex items-center truncate',
              radix.selectValue?.className,
            )}
          >
            {valueDisplay}
          </div>
        </Select.Value>
        <Select.Icon
          {...radix.selectIcon}
          className={cn(
            'flex items-center text-default',
            radix.selectIcon?.className,
            {
              'text-disabled': disabled,
            },
          )}
        >
          <div
            className={cn(
              chevron({
                state: isOpen ? 'open' : 'default',
              }),
              'mr-sm',
            )}
          >
            <Icon size="md" type="chevron-down" />
          </div>
        </Select.Icon>
      </Select.Trigger>
    );
  },
);

DropdownTrigger.displayName = 'DropdownTrigger';
